/**
 * 预览会话对话框（Wave 4 R3 / T4.7 集成）。
 *
 * 生命周期"预览"入口（ARCHITECTURE_DESIGN.md §5.6）：创建预览会话
 * （POST /debugpavilion/preview）→ 展示含 token 的分享链接（可复制）；
 * 若该应用已有生成产物（output_location），提供产物文件树浏览
 * （GET /debugpavilion/preview/{id}/files，点击文件查看内容）。
 */
import {
  Check,
  Copy,
  ExternalLink,
  FileIcon,
  Folder,
  Loader2,
  RefreshCw,
  Share2,
} from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  createPreviewSession,
  getPreviewFiles,
  type PreviewFileEntry,
  type PreviewSession,
} from "@/services/debugpavilion";
import { buildFileTree, type FileTreeNode } from "@/lib/builder/preview-files";
import { cn } from "@/lib/utils";

export interface PreviewDialogProps {
  appId: string | null;
  appName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function FileTree({ nodes, onSelect, activePath }: { nodes: FileTreeNode[]; onSelect: (path: string) => void; activePath: string | null }) {
  return (
    <ul className="space-y-0.5 text-sm">
      {nodes.map((node) => (
        <li key={node.path}>
          <button
            type="button"
            data-file-path={node.path}
            onClick={() => node.type === "file" && onSelect(node.path)}
            className={cn(
              "flex w-full items-center gap-2 rounded px-2 py-1 text-left font-mono text-xs transition-colors",
              node.type === "file" && "hover:bg-muted",
              node.type === "dir" && "font-medium text-muted-foreground",
              activePath === node.path && "bg-primary/10 text-primary",
            )}
          >
            {node.type === "dir" ? (
              <Folder className="h-3.5 w-3.5 shrink-0 text-amber-500" />
            ) : (
              <FileIcon className="h-3.5 w-3.5 shrink-0 text-sky-500" />
            )}
            <span className="truncate">{node.name}</span>
            {node.type === "file" && node.size !== undefined && (
              <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                {node.size < 1024 ? `${node.size} B` : `${(node.size / 1024).toFixed(1)} KB`}
              </span>
            )}
          </button>
          {node.children.length > 0 && (
            <div className="ml-3 border-l pl-2">
              <FileTree nodes={node.children} onSelect={onSelect} activePath={activePath} />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export function PreviewDialog({ appId, appName, open, onOpenChange }: PreviewDialogProps) {
  const [session, setSession] = React.useState<PreviewSession | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [files, setFiles] = React.useState<PreviewFileEntry[] | null>(null);
  const [filesLoading, setFilesLoading] = React.useState(false);
  const [filesError, setFilesError] = React.useState<string | null>(null);
  const [activePath, setActivePath] = React.useState<string | null>(null);
  const [content, setContent] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  const loadFiles = React.useCallback(async (sessionId: string) => {
    setFilesLoading(true);
    setFilesError(null);
    try {
      const result = await getPreviewFiles(sessionId);
      setFiles(result.files ?? null);
    } catch (err) {
      setFilesError(String(err));
      setFiles(null);
    } finally {
      setFilesLoading(false);
    }
  }, []);

  const createSession = React.useCallback(async () => {
    if (!appId) return;
    setCreating(true);
    setError(null);
    setSession(null);
    setFiles(null);
    setActivePath(null);
    setContent(null);
    try {
      const s = await createPreviewSession(appId);
      setSession(s);
      if (s.output_location) {
        void loadFiles(s.id);
      }
    } catch (err) {
      setError(`预览会话创建失败：${String(err)}`);
    } finally {
      setCreating(false);
    }
  }, [appId, loadFiles]);

  React.useEffect(() => {
    if (open && appId) {
      void createSession();
    }
  }, [open, appId, createSession]);

  const handleSelectFile = async (path: string) => {
    if (!session) return;
    setActivePath(path);
    setContent(null);
    try {
      const result = await getPreviewFiles(session.id, path);
      setContent(result.content ?? "");
    } catch (err) {
      setContent(`读取失败：${String(err)}`);
    }
  };

  const handleCopy = async () => {
    if (!session?.preview_url) return;
    try {
      await navigator.clipboard.writeText(session.preview_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // 剪贴板不可用（如非安全上下文）：提示用户手动复制
      setError("浏览器未授权剪贴板，请手动复制链接。");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-indigo-600" />
            预览与分享 · {appName}
          </DialogTitle>
          <DialogDescription>
            创建带分享 token 的预览链接；若应用已有生成产物，可浏览产物文件。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4" data-testid="preview-dialog">
          {error && <p className="text-sm text-destructive">{error}</p>}

          {!session && !creating && (
            <Button onClick={() => void createSession()}>
              <Share2 className="mr-1.5 h-4 w-4" />
              创建预览会话
            </Button>
          )}
          {creating && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              正在创建预览会话...
            </div>
          )}

          {session && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>分享链接（含 token，可分享给协作者）</Label>
                <div className="flex gap-2">
                  <Input readOnly value={session.preview_url} data-testid="preview-url" className="font-mono text-xs" />
                  <Button size="sm" variant="outline" className="shrink-0" data-testid="copy-preview-url" onClick={() => void handleCopy()}>
                    {copied ? <Check className="mr-1 h-3.5 w-3.5 text-green-600" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                    {copied ? "已复制" : "复制"}
                  </Button>
                  <Button size="sm" variant="outline" className="shrink-0" asChild>
                    <a href={session.preview_url} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1 h-3.5 w-3.5" />
                      打开
                    </a>
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  会话过期时间：
                  {session.expires_at ? new Date(session.expires_at).toLocaleString("zh-CN") : "24 小时"}
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>产物文件</Label>
                  <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => void loadFiles(session.id)}>
                    <RefreshCw className="h-3 w-3" />
                    刷新
                  </Button>
                </div>
                {filesLoading && (
                  <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    加载产物文件树...
                  </div>
                )}
                {!filesLoading && filesError && (
                  <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground" data-testid="preview-files-empty">
                    {session.output_location
                      ? `产物目录读取失败：${filesError}`
                      : "该应用还没有成功生成的产物，暂时没有文件可浏览。先执行一次生成（AI 面板 → 构建模式）再回来。"}
                  </p>
                )}
                {!filesLoading && !filesError && files && (
                  <div className="max-h-56 overflow-auto rounded-md border bg-card p-2" data-testid="preview-files">
                    {files.length === 0 ? (
                      <p className="p-2 text-xs text-muted-foreground">产物目录为空。</p>
                    ) : (
                      <FileTree nodes={buildFileTree(files)} onSelect={(p) => void handleSelectFile(p)} activePath={activePath} />
                    )}
                  </div>
                )}
                {activePath && content !== null && (
                  <pre
                    className="max-h-56 overflow-auto rounded-md border bg-muted/50 p-3 font-mono text-[11px] leading-relaxed"
                    data-testid="preview-file-content"
                  >
                    {content}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
